/**
 * Team Availability Calendar (LEAVE-REGISTRY-P4)
 * Managers/HR/Admin: who is on approved leave over the next weeks.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, ChevronLeft, ChevronRight, Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const TeamAvailability = () => {
  const [offset, setOffset] = useState(0); // weeks from current
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const rangeStart = new Date();
  rangeStart.setDate(rangeStart.getDate() - rangeStart.getDay() + 1 + offset * 14);
  const rangeEnd = new Date(rangeStart);
  rangeEnd.setDate(rangeEnd.getDate() + 13);

  const startIso = iso(rangeStart);
  const endIso = iso(rangeEnd);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/leave-registry/team-availability`, {
        params: { start: startIso, end: endIso }
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load team availability', err);
      setError(err.response?.data?.detail || 'Failed to load team availability. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [startIso, endIso]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const days = [];
  const d = new Date(rangeStart);
  while (d <= rangeEnd) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  const holidaySet = new Set((data?.holidays || []).map(h => h.date));

  return (
    <div className="space-y-4" data-testid="team-availability">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Team Availability</h3>
          <span className="text-sm text-gray-500">{startIso} → {endIso}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOffset(o => o - 1)} data-testid="availability-prev" className="p-1.5 border rounded-lg hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setOffset(0)} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">Today</button>
          <button onClick={() => setOffset(o => o + 1)} data-testid="availability-next" className="p-1.5 border rounded-lg hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-10 border border-red-100 bg-red-50 rounded-lg" data-testid="team-availability-error">
          <AlertCircle className="w-6 h-6 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={fetchData}
            data-testid="team-availability-retry"
            className="flex items-center gap-2 px-3 py-1.5 border border-red-300 text-red-700 rounded-lg text-sm hover:bg-red-100"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="tf-thead">
                <th className="sticky left-0 bg-slate-50 px-3 py-2 text-left font-medium text-gray-600 min-w-[160px]">Member</th>
                {days.map(day => {
                  const di = iso(day);
                  const wk = day.getDay() === 0 || day.getDay() === 6;
                  return (
                    <th key={di} className={`px-1.5 py-2 text-center font-medium ${wk || holidaySet.has(di) ? 'text-gray-300' : 'text-gray-600'}`}>
                      {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional weekday label */}
                      <div>{day.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)}</div>
                      <div>{day.getDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.members || []).map(m => {
                const leaveMap = {};
                (m.leave_days || []).forEach(ld => { leaveMap[ld.date] = ld; });
                return (
                  <tr key={m.id}>
                    <td className="sticky left-0 bg-white px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{m.full_name}</td>
                    {days.map(day => {
                      const di = iso(day);
                      const wk = day.getDay() === 0 || day.getDay() === 6;
                      const hol = holidaySet.has(di);
                      const lv = leaveMap[di];
                      let cls = 'bg-emerald-50';
                      let title = 'Available';
                      if (wk) { cls = 'bg-gray-100'; title = 'Weekend'; }
                      else if (hol) { cls = 'bg-blue-100'; title = 'Company Holiday'; }
                      if (lv) {
                        cls = lv.paid === false ? 'bg-slate-400' : (lv.day_part !== 'full' ? 'bg-amber-300' : 'bg-red-300');
                        title = `${lv.leave_type} ${lv.day_part !== 'full' ? '(half day)' : ''}${lv.paid === false ? ' — unpaid' : ''}`;
                      }
                      return <td key={di} title={`${di}: ${title}`} className={`px-1.5 py-2 text-center ${cls}`}></td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-4 text-xs text-gray-500">
        <span><span className="inline-block w-3 h-3 bg-emerald-50 border mr-1 align-middle"></span>Available</span>
        <span><span className="inline-block w-3 h-3 bg-red-300 mr-1 align-middle"></span>On Leave</span>
        <span><span className="inline-block w-3 h-3 bg-amber-300 mr-1 align-middle"></span>Half Day</span>
        <span><span className="inline-block w-3 h-3 bg-slate-400 mr-1 align-middle"></span>Unpaid Leave</span>
        <span><span className="inline-block w-3 h-3 bg-blue-100 mr-1 align-middle"></span>Holiday</span>
      </div>
    </div>
  );
};

export default TeamAvailability;
